// **React Imports
import { memo, useCallback, useMemo, useState } from 'react'

// **MUI Imports
import { Box, Button, Skeleton, Typography } from '@mui/material'

// **Component Imports
import DemoGuard from '../demo-guard'
import OverlayLoading from '../overlay-loading'

// **Third party Imports
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'

// **Utils Imports
import { showErrorToast } from '../../utils/utils'

// **API Imports
import { markCandidateStatus } from '../../axios/api/contest'

const RejectQualifyCandidate = ({
  data,
  refreshHandler = () => {},
  loading: componentLoading = false
}) => {
  // hooks
  const { attemptId } = useParams()

  // states
  const [loading, setLoading] = useState(false)

  // variables
  const prefixText = useMemo(() => {
    return data?.modifiedBy || null
  }, [data?.modifiedBy])

  // handlers
  const handleQualify = useCallback(async () => {
    const attemptIds = [attemptId]

    if (attemptIds?.length > 0) {
      await Swal.fire({
        icon: 'info',
        text: `Are you sure you want to qualify the candidate?`,
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        showCancelButton: true
      }).then(async (result) => {
        if (result.isConfirmed) {
          const payload = {
            attemptIds: attemptIds,
            action: 'QUALIFY'
          }
          setLoading(true)
          const response = await markCandidateStatus(payload)
          setLoading(false)

          if (response?.isError) {
            showErrorToast(response)
          } else {
            toast.success('Candidate qualified')
            refreshHandler()
          }
        }
      })
    }
  }, [attemptId, refreshHandler])

  const contestRejectCandidate = useCallback(
    async (payload) => {
      setLoading(true)
      const response = await markCandidateStatus(payload)
      setLoading(false)

      if (response?.isError) {
        showErrorToast(response)
      } else {
        toast.success(response?.message || 'Candidate(s) Rejected Successfully')
        refreshHandler()
      }
    },
    [refreshHandler]
  )

  const handleReject = useCallback(async () => {
    const result = await Swal.fire({
      icon: 'info',
      text: `Are you sure you want to reject this candidate?`,
      confirmButtonText: 'Yes, Reject',
      cancelButtonText: 'Cancel',
      showCancelButton: true,
      input: 'textarea',
      inputPlaceholder: 'Type your reason here...',
      inputAttributes: {
        'aria-label': 'Type your reason here'
      },
      inputValidator: (value) => {
        if (!value || value.trim().length === 0) {
          return 'Please provide a reason for rejection.'
        }
      }
    })

    if (!result.isConfirmed) return

    const feedback = result?.value || ''

    const attemptIds = [attemptId]
    const payload = {
      attemptIds: attemptIds,
      action: 'REJECT',
      rejectionFeedback: feedback
    }

    contestRejectCandidate(payload)
  }, [contestRejectCandidate, attemptId])

  return (
    <>
      {loading && <OverlayLoading />}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}
      >
        {componentLoading ? (
          <Skeleton
            width={'150px'}
            height={'20px'}
            sx={{ transform: 'scale(1,1)' }}
          />
        ) : (
          <>
            {prefixText && (
              <Typography fontWeight={'700'} fontSize={'14px'}>
                {prefixText}
              </Typography>
            )}
            {data?.showQualifyButton && (
              <DemoGuard>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={handleQualify}
                  sx={{ fontSize: '12px' }}
                >
                  Qualify
                </Button>
              </DemoGuard>
            )}
            {data?.showRejectButton && (
              <DemoGuard>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={handleReject}
                  sx={{ fontSize: '12px' }}
                >
                  Reject
                </Button>
              </DemoGuard>
            )}
          </>
        )}
      </Box>
    </>
  )
}

export default memo(RejectQualifyCandidate)
